import { CalendarStore, IExclude, RootStore } from "@lib/store";
import { MSCalendar } from "../..";
import { UiFloatModal } from "../../../common/modal";
import { UiFloatPopover } from "../../../common/popover";
import {
  createNewEvent,
  initTypeSelect,
  validateBACnetValue,
} from "../modal.helpers";
import { EBACnetDataTypes, ITooltipElement } from "../modal.types";
import {
  getAddEventTemplate,
  getEditEventTemplate,
  getOptionTemplateByType,
  popupEvent,
} from "../modal.templates";
import { EPeriodTypes, IRenderOptionByTypeProps } from "./exception.types";
import { getFormError, initSelectOptions } from "./exception.helpers";
import { IEvent } from "@lib/store/src/calendar";
import { SelectHelper } from "./exception-form-helper";
import i18next from "i18next";

/**
 * Базовый класс добавления исключения
 * @class ExceptionModal
 * @extends Basic
 * @classdesc ExceptionModal
 * */
export class ExceptionModal {
  root: MSCalendar;
  rootID: string;
  store: CalendarStore;
  popup: UiFloatModal;
  bodyEvents: HTMLElement;
  eventsState: Array<IEvent>;
  period: EPeriodTypes;
  periodForm: HTMLElement;
  select: HTMLSelectElement;
  selectHelper: SelectHelper;
  priority: HTMLInputElement;

  constructor(rootID: string, root: MSCalendar) {
    this.root = root;
    this.rootID = rootID;
    this.eventsState = [];
    this.period = EPeriodTypes.DATE;
    this.selectHelper = new SelectHelper(() => {
      this.validateForms();
    });
  }

  /**
   * Установка шаблона PopUp и вызов начальных методов
   * @param template
   * @param styles
   * @returns
   */
  init(template: string, styles?: any) {
    if (!template) return;

    try {
      this.popup = new UiFloatModal(template, true, { bottom: true, right: true, left: true }, { width: 578, height: 300 });
      if (styles) {
        this.popup.modalComponent.classList.innerHTML += styles;
      }
      this.popup.modalComponent.x = 10;
      this.popup.modalComponent.y = 10;
      this.popup.modalComponent.minWidth = 578;

      // Init
      this.initVars();
      this.onStartup();
      this.initStore();
      this.initPeriod();
    } catch (e) {
      console.log("Error <initPopup> >> ", e);
    }
  }

  initVars() {
    const modal = this.popup.modalComponent;
    this.bodyEvents = modal.querySelector("#events");

    this.periodForm = modal.querySelector("#period-form");
    this.select = modal.querySelector("#select-period");
    this.priority = modal.querySelector("#priority"); 
  }

  initStore() {
    this.store = RootStore.calendars.find(this.rootID);
  }

  validateForms() {
    if (!this.isValidForm()) {
      return;
    }
    this.isValidPeriodForm();
  }
 
  /**
   * Инициализация селекта "период"
   */
  initPeriod(exclude?: IExclude) {
    // Пересоздаем элемент (сброс всех слушателей)
    const newSelect = this.select.cloneNode(true) as HTMLSelectElement;
    this.select.replaceWith(newSelect);
    this.select = newSelect;
    // this.select.disabled = Boolean(exclude);

    // Если исключение существует - подставляем значение
    initSelectOptions(this.select, exclude?.period?.type || EPeriodTypes.DATE);
    this.period = exclude?.period?.type || EPeriodTypes.DATE;

    // При смене типа данных - ререндер ячейки значения
    this.select?.addEventListener("change", (e: Event) => {
      this.period = Number(e.target.value);
      
      // если выбираем изначальный тип данных при редактировании
      if (this.period === exclude?.period?.type) {
        this.renderForm(exclude);
      } else {
        this.renderForm();
      }
      this.validateForms();
    });

    this.renderForm(exclude);
  }

  renderForm = (exclude?: IExclude) => {
    const formContainer = document.createElement("div");
    formContainer.id = "period-form";
    this.periodForm.replaceWith(formContainer);
    this.periodForm = formContainer;

    // Рендер формы по типу диапазона
    this.selectHelper.renderForm(
      this.period,
      this.periodForm,
      formContainer,
      exclude?.period || null
    );
  };

  /**
   * Инициализация обработчиков событий
   */
  onStartup() {}

  /**
   * Сброс формы до начальных значений
   */
  resetForm() {
    // Очищаем хранилище данных
    this.eventsState = [];

    // Удаляем все добавленные элементы из контейнера событий
    this.bodyEvents.innerHTML = "";

    this.periodForm.innerHTML = "";

    this.initPeriod();
  }

  /**
   * Обработчик кликов по зоне событий
   * @param {CustomEvent} e CustomEvent
   */
  onClickEvent(e: MouseEvent) {
    const target = e.target as HTMLButtonElement;

    // Удаляем строку события по клику на крестик
    if (target.classList.contains("delete-btn")) {
      const row = target.closest(".grid-row") as HTMLElement;
      if (!row) return;

      this.showDeleteConfirmationPopup(e, row);
    }
  }

  /**
   * Показ попапа подтверждения удаления
   * @param {CustomEvent} e CustomEvent
   * @param {HTMLElement} row Строка события
   */
  showDeleteConfirmationPopup(e: MouseEvent, row: HTMLElement) {
    setTimeout(() => {
      const popover = new UiFloatPopover(
        popupEvent({
          title: i18next.t($ => $.scheduleBacnet.deletion),
          desc: i18next.t($ => $.scheduleBacnet.eventDeleteConfirmation),
        }),
        {
          x: e.clientX,
          y: e.clientY,
        }
      );

      popover.root.querySelector("#primary")?.addEventListener("click", () => {
        this.eventsState = this.eventsState?.filter(
          (week) => week.id !== row.dataset.eventId
        );

        row.remove();
        popover.destroy();
        this.validateForms();
      });

      popover.root
        .querySelector("#secondary")
        ?.addEventListener("click", () => popover.destroy());
    }, 0);
  }

  /**
   * Отрисовка строки события
   * @param {IEvent} editEvent Данные события для отрисовки
   * */
  addEventTime(editEvent?: IEvent) {
    const event: IEvent = editEvent || createNewEvent();
    const element = document.createElement("div");
    element.classList.add("grid-row");
    if (editEvent) {
      element.innerHTML = getEditEventTemplate(event);
    } else {
      element.innerHTML = getAddEventTemplate();
    }
    element.dataset.eventId = event?.id;
    this.setUpEventHandlers(element, event, editEvent != null);
    this.eventsState.push(event);
    this.bodyEvents.appendChild(element);
    this.validateForms();
  }

  /**
   * Обработчики значений в строке добавления событий
   * @param {Element} element Новая строка добавления события
   * @param {IWeek} event Ссылка на экземпляр нового события
   * */
  setUpEventHandlers(element: HTMLElement, event: IEvent, isEdit: boolean = false) {
    const selectType = initTypeSelect(
      element.querySelector("#select-type"),
      event
    );
    let blockValue = element.querySelector("#block-value");

    const addOption = (value?: string | number) => {
      const newBlockValue = document.createElement("div");
      newBlockValue.id = "block-value";
      blockValue.replaceWith(newBlockValue);
      blockValue = newBlockValue;
      event.value = event.dataType === 1 && !isEdit ? '0' : (value ?? null);

      // Рендер ячейки события по типу
      this.renderOptionByType({
        blockValue,
        dataType: event.dataType,
        value: event.value,
        changeValue: (value) => {
          event.value = value ? String(value) : null;
          event.length = value ? String(value).length : 0;
          this.validateForms();
        },
      });
    };

    // При смене типа данных - ререндер ячейки значения
    selectType?.addEventListener("change", (e: Event) => {
      event.dataType = Number(e.target.value);
      addOption();
      this.validateForms();
    });

    // Для режима редактирования - отрисовываем заполненное значение
    if (event.dataType !== null) {
      addOption(event.value);
    }

    // Добавляем обработчик выбора времени события
    element
      .querySelector("#time")
      ?.addEventListener("input", (e: Event) => {
        const [hour, minutes, seconds] = e?.target?.value?.split(":");
        event.time.hours = hour || "";
        event.time.minutes = minutes || "";
        event.time.seconds = seconds || "";
        this.validateForms();
      });
  }

  /**
   * Отрисовка поля ввода данных по типу
   *  @param {Element} blockValue fw-select для заполнения типами данных
   *  @param {Number} dataType Номер типа данных из EBACnetDataTypes
   *  @param {Function} changeValue Callback для обработки полученных значений
   *  @param {string} value Callback для обработки полученных значений
   */
  private renderOptionByType({
    blockValue,
    dataType,
    changeValue,
    value,
  }: IRenderOptionByTypeProps) {
    blockValue.innerHTML = getOptionTemplateByType(dataType, value);

    const onFwChange = (value: CustomEvent) => {
      const val = value?.detail?.value ?? "";
      const err = validateBACnetValue(val, dataType);

      if (err) {
        blockValue.classList.add("error");
      } else {
        blockValue.classList.remove("error");
      }

      this.toggleTooltip(blockValue.querySelector("#event-tooltip"), err);
      changeValue(val);
    };

    switch (dataType) {
      case EBACnetDataTypes.NULL: {
        changeValue(value ?? 0);
        break;
      }

      case EBACnetDataTypes.BOOLEAN: {
        changeValue(value ?? 0);

        blockValue?.addEventListener("fwChange", (value: CustomEvent) => {
          changeValue(Number(value?.detail?.meta?.checked).toString()); // bool -> string
        });
        break;
      }

      case EBACnetDataTypes.UNSIGNED_INTEGER:
      case EBACnetDataTypes.SIGNED_INTEGER:
      case EBACnetDataTypes.REAL:
      case EBACnetDataTypes.DOUBLE: {
        // Валидация введенных данных
        blockValue?.addEventListener("fwInput", onFwChange);
        break;
      }

      case EBACnetDataTypes.ENUMERATION: {
        if (!blockValue?.querySelector('#event-value')) return; 
        const enumValues: Record<string, string> = structuredClone(this.root.enumRef);

        // Если пришло неизвестное значение -> Добавляем в список перечислений
        if (value !== "" && value !== null && value !== undefined && !enumValues[value]) {
          Object.assign(enumValues, {
            [value]:  value
          })
        }

        const select = initSelectOptions(blockValue?.querySelector('#event-value'), value, Object.entries(enumValues)); 
        
        select.addEventListener("change", (e: Event) => {
          const val = e.target.value ?? "";
          const err = validateBACnetValue(val, dataType);
    
          if (err) {
            blockValue.classList.add("error");
          } else {
            blockValue.classList.remove("error");
          }
    
          this.toggleTooltip(blockValue.querySelector("#event-tooltip"), err);
          changeValue(val);
        })

        break;
      }

      default:
        blockValue.innerHTML = getOptionTemplateByType(dataType);
        break;
    }
  }

  /**
   * Переключение кастомного попапа
   *  @param {ITooltipElement} tooltip fw-tooltip элемент
   *  @param {string} textError Текст ошибки
   */
  private toggleTooltip(tooltip: ITooltipElement, textError: string) {
    if (!tooltip) return;
    if (!textError) {
      tooltip.setAttribute("content", "");
      return;
    }

    tooltip.setAttribute("content", textError);

    // Custom tooltip
    return setTimeout(() => {
      const tooltipRoot = tooltip.shadowRoot.querySelector(
        ".tooltip"
      ) as HTMLElement;
      if (tooltipRoot) {
        tooltipRoot.style.color = "#000";
        tooltipRoot.style.backgroundColor = "#FFFACD";
      }
    }, 0);
  }

  /**
   * Валидация формы на факт заполнения всех полей события
   * */
  isValidForm() {
    const error = getFormError(this.eventsState);

    this.toggleTooltip(
      this.popup.modalComponent.querySelector("#tooltip"),
      error
    );

    const submitButton = this.popup.modalComponent.querySelector('#submit') as HTMLButtonElement;

    if (error) {
      submitButton.disabled = true;
      return false
    };

    submitButton.disabled = false;
    return true;
  }

  /**
   * Валидация формы на факт заполнения корректности периода
   * */
  isValidPeriodForm() {
    const error = this.selectHelper.isValidFields(this.store.date);

    this.toggleTooltip(
      this.popup.modalComponent.querySelector("#tooltip"),
      error
    );

    const submitButton = this.popup.modalComponent.querySelector('#submit') as HTMLButtonElement;

    if (error) {
      submitButton.disabled = true;
      return false
    };

    submitButton.disabled = false;
    return true;
  }

  /**
   * Переключение видимости модального окна
   */
  toggle() {
    this.popup.modalComponent.toggle();
    if (!this.popup.modalComponent.active) return;
  }

  /**
   * Открытие модального окна
   */
  open() {
    this.popup.modalComponent.open();

    this.resetForm(); // Сброс формы
    this.popup.uiModal.resetSize();
  }

  /**
   * Закрытие модального окна
   */
  close() {
    this.popup.modalComponent.close();
  }
}
